/**
 * AI Runner - Orchestratore per Marta
 *
 * Due modalità di funzionamento:
 *
 * 1. SIMPLE MODE (default): Local intent detection
 *    - Rileva intento con keyword matching
 *    - Recupera dati dal DB
 *    - Inietta nel prompt per LLM
 *
 * 2. ORCHESTRATOR MODE (quando configurato): Cloud AI + local extraction
 *    - Sanitizza messaggio (rimuove PII)
 *    - Usa Cloud AI (GPT-4/Claude) per analizzare la struttura
 *    - Estrae entità dal messaggio originale
 *    - Esegue operazioni GraphQL
 *    - Privacy: il cloud non vede MAI i dati sensibili
 */

import { detectIntent, requiresDataFetch, type DetectedIntent } from './intentDetector';
import { fetchDataForIntent, type FetchedData } from './dataFetcher';
import {
  loadAIConfig,
  isOrchestratorAvailable,
  toCloudClientConfig,
  type AIConfig,
} from '../orchestrator/config';

export { detectIntent, requiresDataFetch, fetchDataForIntent };
export type { DetectedIntent, FetchedData };

// Lazy-load orchestrator to avoid loading cloud client when not needed
let orchestratorModule: typeof import('../orchestrator') | null = null;

async function getOrchestratorModule() {
  if (!orchestratorModule) {
    orchestratorModule = await import('../orchestrator');
  }
  return orchestratorModule;
}

/**
 * Risultato del preprocessing della domanda
 */
export interface PreprocessResult {
  intent: DetectedIntent;
  fetchedData: FetchedData | null;
  contextInjection: string; // Testo da aggiungere al prompt
}

/**
 * Preprocessa la domanda dell'utente
 *
 * Rileva l'intento e recupera i dati necessari dal DB.
 * Ritorna il contesto da iniettare nel prompt per il LLM.
 */
export async function preprocessQuery(query: string): Promise<PreprocessResult> {
  console.log('[Runner] Preprocessing query:', query);

  // 1. Rileva l'intento
  const intent = detectIntent(query);
  console.log('[Runner] Detected intent:', intent.domain, intent.operation, `(confidence: ${intent.confidence.toFixed(2)})`);

  // 2. Se l'intento richiede dati, recuperali
  let fetchedData: FetchedData | null = null;
  let contextInjection = '';

  if (requiresDataFetch(intent)) {
    console.log('[Runner] Fetching data for intent...');
    fetchedData = await fetchDataForIntent(intent);

    if (fetchedData) {
      console.log('[Runner] Data fetched:', fetchedData.count, 'items');

      // Costruisci il contesto da iniettare
      contextInjection = `
## DATI DAL DATABASE (GIÀ RECUPERATI PER TE)

**IMPORTANTE**: Questi dati sono già stati recuperati dal database.
NON dire "non ho accesso" - HAI accesso e i dati sono qui sotto.
Rispondi direttamente usando questi dati.

${fetchedData.summary}

---

**Istruzioni**:
- Rispondi in modo naturale e diretto usando i dati sopra
- NON usare tools per questa domanda - i dati sono già qui
- NON dire "non ho accesso a informazioni personalizzate" - ce le HAI
- Presenta i dati in modo chiaro e organizzato
`;
    }
  } else {
    console.log('[Runner] No data fetch required (general chat)');
  }

  return {
    intent,
    fetchedData,
    contextInjection
  };
}

/**
 * Costruisce il system prompt con i dati iniettati
 */
export function buildPromptWithData(basePrompt: string, contextInjection: string): string {
  if (!contextInjection) {
    return basePrompt;
  }

  return `${basePrompt}

${contextInjection}`;
}

/**
 * System prompt semplificato per Marta (senza tool calling)
 */
export const MARTA_SIMPLE_PROMPT = `Sei Marta, un'assistente business intelligente e proattiva.

## PERSONALITÀ
- Professionale ma amichevole
- Efficiente e orientata ai risultati
- Chiara e concisa nelle risposte

## COMPORTAMENTO
- Rispondi sempre in italiano
- Sii diretta e vai al punto
- Basa le tue risposte SOLO sui dati forniti
- NON inventare mai dati, progetti, task, contatti o altri elementi
- Se non hai dati sufficienti, dillo chiaramente

## CONTESTO TEMPORALE
Oggi è ${new Date().toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.`;

// =============================================================================
// ORCHESTRATOR MODE
// =============================================================================

/**
 * Verifica se l'orchestrator è disponibile
 */
export function isOrchestratorEnabled(): boolean {
  return isOrchestratorAvailable();
}

/**
 * Ottiene la configurazione AI corrente
 */
export function getAIConfig(): AIConfig {
  return loadAIConfig();
}

/**
 * Processa una richiesta usando l'orchestrator (Cloud AI + Local extraction)
 *
 * Questo metodo usa il pattern Privacy-First:
 * 1. Il messaggio viene sanitizzato (PII rimossi)
 * 2. Cloud AI analizza la struttura (non vede dati reali)
 * 3. Estrae le entità dal messaggio originale
 * 4. Viene eseguita l'operazione GraphQL
 *
 * @param userMessage - Messaggio originale dell'utente
 * @param context - Contesto opzionale (vista corrente, progetto selezionato, etc.)
 * @param onMutation - Callback opzionale per eseguire mutation GraphQL (default: usa graphqlExecutor)
 * @param onQuery - Callback opzionale per eseguire query GraphQL (default: usa graphqlExecutor)
 */
export async function processWithOrchestrator(
  userMessage: string,
  context?: {
    currentView?: string;
    selectedProject?: string;
    selectedContact?: string;
  },
  onMutation?: (name: string, variables: Record<string, unknown>) => Promise<unknown>,
  onQuery?: (name: string, variables: Record<string, unknown>) => Promise<unknown>
): Promise<{
  success: boolean;
  response: string;
  action?: {
    type: 'mutation' | 'query';
    name: string;
    variables: Record<string, unknown>;
    result?: unknown;
  };
  fromCache?: boolean;
  error?: string;
}> {
  const config = loadAIConfig();

  if (!config.cloud) {
    throw new Error('Orchestrator non configurato. Aggiungi API key cloud nelle impostazioni.');
  }

  const orchestrator = await getOrchestratorModule();
  const cloudConfig = toCloudClientConfig(config);

  if (!cloudConfig) {
    throw new Error('Configurazione cloud non valida.');
  }

  // Use default GraphQL executor if no custom callbacks provided
  const defaultMutationExecutor = async (name: string, vars: Record<string, unknown>) => {
    return orchestrator.executeMutation(name, vars);
  };

  const defaultQueryExecutor = async (name: string, vars: Record<string, unknown>) => {
    return orchestrator.executeQuery(name, vars);
  };

  // Initialize orchestrator with config
  const ai = orchestrator.initOrchestrator({
    cloud: cloudConfig,
    schema: orchestrator.getDefaultSchema(),
    onExecuteMutation: onMutation || defaultMutationExecutor,
    onExecuteQuery: onQuery || defaultQueryExecutor,
  });

  // Process the request
  return ai.process({
    userMessage,
    context,
  });
}

/**
 * Ottieni statistiche dell'orchestrator (cache hits, top patterns, etc.)
 */
export async function getOrchestratorStats(): Promise<{
  cacheSize: number;
  cacheHits: number;
  topPatterns: Array<{ pattern: string; usage: number }>;
} | null> {
  if (!isOrchestratorAvailable()) {
    return null;
  }

  try {
    const orchestrator = await getOrchestratorModule();
    const ai = orchestrator.getOrchestrator();
    return ai.getStats();
  } catch {
    return null;
  }
}

// Re-export orchestrator config functions for easy access
export {
  loadAIConfig,
  isOrchestratorAvailable,
  toCloudClientConfig,
} from '../orchestrator/config';

export type { AIConfig } from '../orchestrator/config';
